import type {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'

import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import Container from '~/components/atoms/Container/Container'
import AsSeenIn from '~/components/blocks/AsSeenIn/AsSeenIn'
import type { FeaturedArticlesProps } from '~/components/blocks/FeaturedArticles/FeaturedArticles'
import PressHeader from '~/components/press/PressHeader/PressHeader'
import PressNavigation from '~/components/press/PressNavigation/PressNavigation'
import PressRelatedArticles from '~/components/press/PressRelatedArticles/PressRelatedArticles'
import { resolveBlock } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import { supportedLanguages } from '~/data/lang/settings'
import {
  createBaseSelector,
  detectBodyModuleTypes,
  featuredArticleItem,
  getBodyModules,
  internationalizedArrayField,
  internationalizedField,
  myPortfolioBanner,
} from '~/schemas/common'
import { getFAQData } from '~/schemas/JsonLd'
import { useSanity } from '~/services/sanity'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import {
  resolveUrlLinksWithTranslation,
  slugFromContext,
  urlQuery,
} from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/pages.module.css'

interface PageData {
  breadcrumb: BreadcrumbProps['list']
  pressCategories: { name: string; slug: string; order: number }[]
  body: {
    name: string
    summary?: string
    slug: any
    body: any
    categories: {
      name: string
      slug: string
      order?: number
    }[]
  }
  featuredArticles: FeaturedArticlesProps['articles']
  pageModules: any
}

const PressCategory: NextPage<PageData> = (props: PageData) => {
  const {
    body: { name, summary, slug, body: bodyBlocks },
    pressCategories,
    featuredArticles,
  } = props

  // first order pressCategories by order property ascending
  const orderedPressCategories = pressCategories.sort(
    (a, b) => (a?.order || 0) - (b?.order || 0)
  )

  return (
    <div>
      <PressNavigation
        featuredCategories={orderedPressCategories}
        inPage={false}
      />
      <Breadcrumb list={props.breadcrumb} />
      <PressHeader
        name={name}
        summary={summary}
        isPressCategory={true}
        theme={ComponentTheme.primary}
      />

      {bodyBlocks && (
        <div className={styles.body}>
          {/* Filter by type not being featuredArticles */}
          {bodyBlocks
            .filter(
              (item: any) =>
                !(
                  item._type == 'collectionSelector' &&
                  item.value === 'featuredArticles'
                ) &&
                !(
                  item._type == 'collectionSelector' &&
                  item.value === 'pressContactDetails'
                )
            )
            .map((block: any, i: any) => {
              if (i === 0) {
                return (
                  <>
                    {resolveBlock(block, i)}
                    <div className={styles.pressAsSeenin} key={`i-block-${i}`}>
                      <Container>
                        <AsSeenIn noText />
                      </Container>
                      {featuredArticles && featuredArticles.length > 0 && (
                        <div className={styles.pressRelatedArticles}>
                          <PressRelatedArticles
                            category={slug.current}
                            articles={featuredArticles?.slice(0, 12)}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )
              }
              return resolveBlock(block, i)
            })}
        </div>
      )}
    </div>
  )
}

const getStaticPropsOriginal = async function getStaticProps(
  context: GetStaticPropsContext,
  localizedData: {
    settings: any
    allArtists: any
    totalArtists: number
    seoTemplates: any
  }
): Promise<GetStaticPropsResult<any>> {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, scorer, limiter } = await useSanity(context)
    const slug = slugFromContext(context)
    const lang = (context?.params?.lang as Locale) || 'en'
    const messages = await loadMessages(lang)

    // STEP 1: Pre-query to detect which body module types are actually used
    const baseSelector = createBaseSelector(
      'category',
      ['type == "press"', `slug.current == $slug`],
      limiter,
      scorer
    )
    const detectedTypes = await detectBodyModuleTypes(
      client,
      baseSelector,
      { slug },
      ['body', 'pageBuilderModules2']
    )

    // STEP 2: Main query with only the required body modules
    const query = `
        {
          "body": ${baseSelector} [0] {
            "name": ${internationalizedField('name')},
            "summary": ${internationalizedArrayField('summary', lang)},
            slug,
            "body": body[]${resolveUrlLinksWithTranslation(lang)} {
              ${getBodyModules(lang, detectedTypes)}
            },
            "pageBuilderModules2": pageBuilderModules2[]${resolveUrlLinksWithTranslation(lang)} {
              ${getBodyModules(lang, detectedTypes)}
            },
            "categories": associatedCategories[]-> {
              "name": ${internationalizedField('name')},
              "slug": slug.current,
              "order": categoryOrder
            },
          },
          "featuredArticles": *[_type == 'featuredArticles' && primaryCategory->slug.current == $slug] | order(date desc) {
            ${featuredArticleItem}
          },
          "pressCategories": *[_type == 'category' && type == 'press'] {
            "name": ${internationalizedField('name')},
            "slug": slug.current,
            "order": categoryOrder
          },
          ${myPortfolioBanner}
        }`

    const { meta: metaProps, ...data } = await client.fetch(query, {
      slug: slug,
      lang,
    })

    // Check if body exists (category was found)
    if (!data.body) {
      return {
        notFound: true,
      }
    }

    const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
      ['press-and-media', data.body.name],
      '',
      undefined,
      messages,
      lang
    )

    const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

    return {
      props: {
        notFound: false,
        smallHeader: true,
        url: `/press/${slug}`,
        ...data,
        breadcrumb,
        breadcrumbJsonLd,
        hasGoogleReviews: false,
        noAnimatedTheme: true,
        darkFooter: true,
        theme: 'light',
        jsonLd: {
          faqJsonLd: getFAQData(data.body?.body),
        },
      },
    }
  } catch (e) {
    console.log(e)
    return { props: { notFound: true } }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)

    if ('props' in result) {
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

export const getStaticPaths: GetStaticPaths = async () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { client } = await useSanity()
  const pages: Array<{
    url?: {
      _type?: string
      slug?: string
    }
  }> = await client.fetch(
    `*[_type == 'category' && type == 'press' && !(_id in path('drafts.**')) && defined(slug)]{
    ...,
    ${urlQuery},
  }`
  )

  const languages = ['en', ...supportedLanguages]

  const validPaths = pages
    .filter((page: any) => {
      return Boolean(page?.url?.slug) // Type guard to ensure slug exists
    })
    .flatMap((page: any) => {
      return languages.map(lang => ({
        params: {
          lang,
          slug: page.url.slug,
        },
      }))
    })

  return {
    paths: validPaths,
    fallback: 'blocking',
  }
}

export default PressCategory
